import { Dropdown, Space } from 'antd';
import {
  camelCase,
  capitalize,
  get,
  kebabCase,
  keyBy,
  lowerCase,
} from 'lodash';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { FieldType } from 'components/organisms/tasks/fields';
import { ProductiveHeading07 } from 'components/styles';
import { STATUS_FIELD_NAME } from 'data/fields/hooks';
import { useTranslation } from 'hooks/use-translation';
import {
  FIELD_ID_CURRENT_SPRINT,
  FIELD_ID_PROJECT_PHASE_FIELD,
  FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID,
  TaskField,
} from 'stores/fields';
import { FieldDataType, FieldTypes } from '../module-settings/fields/types';

export interface Item {
  label: string;
  value: string;
  fieldId: number;
  isCustomField: boolean;
  isUserField: boolean;
}

export const NONE = '-1';

const computeKanbanBoardRowItems = (
  fields: TaskField[],
  isAllTasksPage: boolean,
): Item[] => {
  return fields
    .filter(({ type, fieldId }) => {
      return (
        (type === FieldType.SingleSelect &&
          fieldId !== FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID &&
          fieldId !== FIELD_ID_CURRENT_SPRINT) ||
        type === FieldType.Assignee ||
        (!isAllTasksPage && fieldId === FIELD_ID_PROJECT_PHASE_FIELD)
      );
    })
    .filter(
      (field) =>
        ![camelCase(STATUS_FIELD_NAME), 'taskDependencyStatus'].includes(
          field.fieldName,
        ),
    )
    .map(({ fieldLabel, fieldName, fieldId, customField, userCreated }) => {
      return {
        label: fieldLabel || capitalize(fieldName),
        value: fieldName,
        fieldId,
        isCustomField: customField || false,
        isUserField: !!userCreated,
      };
    })
    .sort(({ label: a }, { label: b }) => naturalCompare(a, b));
};

const computeKanbanBoardColumnItems = (fields: TaskField[]): Item[] => {
  return fields
    .filter(
      ({
        fieldId,
        fieldType,
        fieldName,
        customField,
        fieldDataType,
        optionsAvailable,
      }) => {
        if (['status', 'priority', 'dueDate'].includes(fieldName)) {
          return true;
        }

        if (
          customField &&
          fieldName.endsWith(fieldId.toString()) && // Want only user-created custom fields
          fieldType === FieldTypes.SingleSelect &&
          (fieldDataType !== FieldDataType.Text || optionsAvailable) && // Want only actual single-choice fields with options
          [
            FieldDataType.Boolean,
            FieldDataType.Date,
            FieldDataType.Text,
          ].includes(fieldDataType as FieldDataType)
        ) {
          return true;
        }

        return false;
      },
    )
    .map(({ fieldLabel, fieldName, fieldId, customField, userCreated }) => {
      return {
        label: fieldLabel || capitalize(fieldName),
        value: fieldName,
        fieldId,
        isCustomField: customField || false,
        isUserField: !!userCreated,
      };
    })
    .sort(({ label: a }, { label: b }) => naturalCompare(a, b));
};

export const useRowItems = (
  fields: TaskField[],
  isAllTasksPage = false,
): Item[] => {
  const none = useTranslation('none');

  return useMemo(() => {
    const result = [
      {
        label: none,
        value: NONE,
        fieldId: -1,
        isCustomField: false,
        isUserField: false,
      },
    ];
    const rowFields = computeKanbanBoardRowItems(fields, isAllTasksPage);

    return [...result, ...rowFields];
  }, [fields, isAllTasksPage, none]);
};

export const useColumnItems = (fields: TaskField[]): Item[] => {
  return useMemo(() => {
    return computeKanbanBoardColumnItems(fields);
  }, [fields]);
};

interface Props {
  items: Item[];
  onChange: (fieldName: string) => void;
  selected: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  disabled?: boolean;
}

const Selected = styled.div`
  ${ProductiveHeading07};
`;

export const GroupByFilter: FC<Props> = ({
  items = [],
  onChange,
  selected,
  label,
  icon,
  disabled,
}) => {
  const hash = keyBy(items, 'value');
  const menu = (
    <Menu
      data-cy="group_by.menu"
      selectedKeys={[selected]}
      onClick={({ key }) => onChange(String(key))}
    >
      {items.map((item) => (
        <Menu.Item
          key={item.value}
          data-cy={`group_by.menu.item.${kebabCase(lowerCase(item.label))}`}
        >
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown overlay={menu}>
      <Button
        icon={icon}
        type="text"
        size="small"
        disabled={disabled}
        data-cy="group_by.trigger"
      >
        <Space align="center" size={4}>
          <div>{label}:</div>
          <Selected>{get(hash, [selected, 'label'])}</Selected>
        </Space>
      </Button>
    </Dropdown>
  );
};

export const GroupByColumnFilter = GroupByFilter;
